import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  getUserHostingQueryKey,
  useHostingAdvancedServerStatus,
} from '@newfold/huapi-js';

import { useAlert } from '~/components/Alerts/alertsStore';
import SideDrawer from '~/components/SideDrawer';
import {
  HOSTING_FETCH_LIMIT,
  HOSTING_LIST_PAGE_SS_KEY,
} from '~/components/TenantProvider/utils/constants';
import { getSelfManagedPlanName } from '~/Nova/scenes/HostingList/utils/helpers';
import { getServerSetupHostnameErrorKey } from '~/utils/hosting/validations';

interface ChangeHostnameModalProps {
  open: boolean;
  onClose: () => void;
  hostingId: number;
  currentHostname?: string;
  serverType?: string | null;
  isSelfManagedDedi?: boolean;
}

const ChangeHostnameModal = ({
  open,
  onClose,
  hostingId,
  currentHostname = '',
  serverType = null,
  isSelfManagedDedi = false,
}: ChangeHostnameModalProps) => {
  const { t } = useTranslation('selfManagedHosting', {
    keyPrefix: 'overview.card.changeHostname',
  });
  const { t: tHostnameValidation } = useTranslation('settings', {
    keyPrefix: 'nameServerAndIPCard.advHostingServerInfo.serverSetup',
  });

  const [hostname, setHostname] = useState(currentHostname);
  const [hasApiHostnameError, setHasApiHostnameError] = useState(false);
  const generateAlert = useAlert();
  const queryClient = useQueryClient();

  const trimmedHostname = hostname.trim();
  const hostnameErrorKey = trimmedHostname
    ? getServerSetupHostnameErrorKey(trimmedHostname, serverType)
    : null;
  const clientErrorMessage = hostnameErrorKey
    ? tHostnameValidation(hostnameErrorKey)
    : undefined;

  useEffect(() => {
    if (open) {
      setHostname(currentHostname);
      setHasApiHostnameError(false);
    }
  }, [open, currentHostname]);

  const onSuccessHostnameChange = () => {
    const nextPage = Number(
      sessionStorage.getItem(HOSTING_LIST_PAGE_SS_KEY) ?? 1,
    );

    queryClient.invalidateQueries({
      queryKey: getUserHostingQueryKey({
        page: 1,
        limit: nextPage * HOSTING_FETCH_LIMIT,
      }),
    });
    queryClient.invalidateQueries({
      queryKey: getUserHostingQueryKey({
        page: nextPage,
        limit: 3,
      }),
    });
  };

  const { mutate: updateServerStatus, isPending } =
    useHostingAdvancedServerStatus({
      mutation: {
        onSuccess: () => {
          generateAlert({
            severity: 'success',
            description: t('successMessage'),
          });
          onSuccessHostnameChange();
          onClose();
        },
        onError: (err) => {
          //@ts-expect-error huapi-js axios error response shape
          if (err?.response?.data?.error === 'invalidHostname') {
            setHasApiHostnameError(true);
            return;
          }
          generateAlert({
            severity: 'error',
            description: t('errorMessageEdit'),
            showCloseBtn: true,
            persist: false,
          });
        },
      },
    });

  const handleUpdateHostname = () => {
    updateServerStatus({
      hostingId,
      data: { action: 'sethostname', hostname: trimmedHostname },
    });
  };

  const handleClose = () => {
    setHostname(currentHostname);
    onClose();
  };

  const handleHostnameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setHostname(event.target.value);
    setHasApiHostnameError(false);
  };

  const isDisabled =
    !trimmedHostname ||
    trimmedHostname === currentHostname.trim() ||
    Boolean(hostnameErrorKey);

  const showFieldError = Boolean(clientErrorMessage) || hasApiHostnameError;
  const helperText = hasApiHostnameError
    ? t('errorMessageInvalidHostname')
    : clientErrorMessage ?? t('hint');

  return (
    <SideDrawer
      open={open}
      onClose={handleClose}
      onCancel={handleClose}
      onSave={handleUpdateHostname}
      title={t('title')}
      buttonLabel={t('confirm')}
      primaryDisabled={isDisabled}
      isLoading={isPending}
      isSideDrawer={false}
      primaryTestId="change-hostname-button"
    >
      <Stack spacing={2}>
        <Typography variant="body2">
          {t('description', {
            hostingPlanType: getSelfManagedPlanName(isSelfManagedDedi),
          })}
        </Typography>
        <TextField
          fullWidth
          value={hostname}
          onChange={handleHostnameChange}
          placeholder={currentHostname}
          size="small"
          error={showFieldError}
          helperText={helperText}
        />
      </Stack>
    </SideDrawer>
  );
};

export default ChangeHostnameModal;
